// Tile Editor Types for Professional Grid-Based Level Editor
import { GRID_CONSTANTS } from './constants/GridConstants';

export const TILE_SIZE = GRID_CONSTANTS.TILE_SIZE;

// Tile Types
export enum TileType {
  EMPTY = 0,
  FLOOR = 1,
  GRASS = 2,
  STONE = 3,
  WATER = 4,
  LAVA = 5,
  WALL = 6,
  DOOR = 7,
  WINDOW = 8,
  HALF_WALL = 9,
  SPAWN_POINT = 10,
  TELEPORTER = 11,
  CHEST = 12,
  LEVER = 13,
  NPC_SPAWN = 14,
  TRAINING_DUMMY = 15,
  TRAP = 16,
  SAFE_ZONE = 17,
  EXIT_ZONE = 18,
  WOOD = 19,       // Wooden platforms (can burn)
  ICE = 20,        // Slippery ice platforms
  ROPE = 21,       // Climbable rope/ladder
}

// Surface Types - Secondary overlay on top of tiles
export enum SurfaceType {
  NONE = 0,
  GRASS = 1,       // Green grass
  SAND = 2,        // Sandy surface
  SNOW = 3,        // Snow cover
  MOSS = 4,        // Mossy growth
  WATER_SHALLOW = 5, // Walkable shallow water
  LAVA_CRACKS = 6,   // Cracked lava
  FLOWERS = 7,     // Flower patch
  VINES = 8,       // Hanging vines
}

// Layer Types
export enum LayerType {
  GROUND = 'ground',
  COLLISION = 'collision',
  OBJECTS = 'objects',
  TRIGGERS = 'triggers',
  ENTITIES = 'entities'
}

// Tile Definition
export interface Tile {
  type: TileType;
  x: number;
  y: number;
  layer: LayerType;
  properties?: Record<string, any>;
  // Secondary surface overlay (covers top ~25% of tile)
  hasSurface?: boolean;
  surfaceType?: SurfaceType;
}

// Layer Data - SPARSE STORAGE
// Uses Map instead of dense 2D array for infinite level support
export interface Layer {
  /** Stable per-layer id (editor add/remove/reorder + React keys). Optional:
   *  saved levels + format converters predate it, so ops fall back to name. */
  id?: string;
  name: LayerType;
  visible: boolean;
  locked: boolean;
  opacity: number;
  // Sparse tile storage: key = "x,y", value = tileType
  // Only stores placed tiles, empty space costs nothing
  tiles: Map<string, number>;
  // Legacy support: dense array for backward compatibility during migration
  data?: number[][];
  entities?: Entity[]; // For entities layer
}

// Entity Types
export interface Entity {
  id: string;
  type: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  properties: Record<string, any>;
  color?: string;
  isOneWay?: boolean;
  velocity?: { x: number; y: number };
}

// Composite Tile Info - stores base+top tile combinations
export interface CompositeTile {
  x: number;
  y: number;
  layer: LayerType;      // Layer where composite exists (usually top tile's layer)
  baseTile: TileType;    // What's underneath
  topTile: TileType;     // What's on top
  behavior: string;      // e.g., 'breakable', 'slippery', 'bouncy'
  durability?: number;   // How many uses before breaking
  properties: Record<string, any>; // Additional behavior properties
}

// Surface overlay data for a specific tile position
export interface TileSurface {
  x: number;
  y: number;
  surfaceType: SurfaceType;
}

// Map Boundaries - Hard left/right limits
export interface MapBoundaries {
  left: number;   // X coordinate of left boundary
  right: number;  // X coordinate of right boundary
  enabled: boolean; // Whether boundaries are active
}

// Grid-Based Level
export interface GridLevel {
  id: string;
  name: string;
  version: string;
  widthTiles: number;  // in tiles
  heightTiles: number; // in tiles
  tileSize: number;
  backgroundColor: string;
  layers: Layer[];
  spawnPoints: { x: number; y: number }[];
  gravity: number;
  // Per-level scene metadata (scene-schema) — terrain anchors + biome binding +
  // scene defaults. Inline import keeps src/shared import order stable.
  scene?: import('./SceneTypes').SceneMeta;
  // Per-level atmosphere (Scene FX v3 M3) — an EffectLayer[] authored in the
  // editor. Opaque here (`unknown[]`) so src/shared stays free of the game-layer
  // atmosphere types; the runtime reads it typed via resolveLayers().
  effectLayers?: unknown[];
  composites?: CompositeTile[]; // NEW: Composite tile data for Phase 1
  surfaces?: TileSurface[]; // NEW: Surface overlay data for tiles
  boundaries?: MapBoundaries; // NEW: Hard left/right boundaries
  /**
   * Optional WC3-style metadata (matches `PlatformerLevel.metadata`). Preserved
   * across `LevelFormatConverter` conversions so per-map flags like
   * `metadata.lobbyTools.enabled` survive editor save / load roundtrips.
   * Typed as `any` to avoid a circular import with `LevelTypes.ts`; the
   * canonical shape is `MapMetadata`.
   */
  metadata?: any;
}

// Editor Tool Types
export enum EditorTool {
  SELECT = 'select',
  BRUSH = 'brush',
  FILL = 'fill',
  ENTITY_PLACER = 'entity_placer', // [NEW]
  RECTANGLE = 'rectangle',
  LINE = 'line',
  ERASER = 'eraser',
  EYEDROPPER = 'eyedropper',
  ENTITY = 'entity'
}

// Brush Settings
export interface BrushSettings {
  size: number; // 1 = single tile, 2 = 2x2, etc.
  shape: 'square' | 'circle';
}

// Editor State
export interface EditorState {
  currentTool: EditorTool;
  currentTile: TileType;
  currentLayer: LayerType;
  brushSettings: BrushSettings;
  gridVisible: boolean;
  snapToGrid: boolean;
  showCollision: boolean;
  zoom: number;
  selectedEntity?: Entity;
}

// Tile Palette Item
export interface TilePaletteItem {
  type: TileType;
  name: string;
  color: string;
  icon?: string;
  category: 'ground' | 'collision' | 'objects' | 'entities' | 'triggers';
}

// Default Tile Palette
export const DEFAULT_TILE_PALETTE: TilePaletteItem[] = [
  // Ground tiles
  { type: TileType.FLOOR, name: 'Floor', color: '#8B7355', category: 'ground' },
  { type: TileType.GRASS, name: 'Grass', color: '#7CFC00', category: 'ground' },
  { type: TileType.STONE, name: 'Stone', color: '#808080', category: 'ground' },
  { type: TileType.WOOD, name: 'Wood', color: '#D2691E', category: 'ground' },
  { type: TileType.WATER, name: 'Water', color: '#1E90FF', category: 'ground' },
  { type: TileType.LAVA, name: 'Lava', color: '#FF4500', category: 'ground' },
  { type: TileType.ICE, name: 'Ice', color: '#E0FFFF', category: 'ground' },
  { type: TileType.ROPE, name: 'Rope', color: '#D2B48C', category: 'ground' },

  // Collision tiles
  { type: TileType.WALL, name: 'Wall', color: '#4A4A4A', category: 'collision' },
  { type: TileType.DOOR, name: 'Door', color: '#8B4513', category: 'collision' },
  { type: TileType.WINDOW, name: 'Window', color: '#87CEEB', category: 'collision' },
  { type: TileType.HALF_WALL, name: 'Half Wall', color: '#696969', category: 'collision' },

  // Object tiles
  { type: TileType.CHEST, name: 'Chest', color: '#DAA520', category: 'objects' },
  { type: TileType.LEVER, name: 'Lever', color: '#A0522D', category: 'objects' },

  // Entity tiles
  { type: TileType.NPC_SPAWN, name: 'NPC Spawn', color: '#00FF00', category: 'entities' },
  { type: TileType.TRAINING_DUMMY, name: 'Training Dummy', color: '#FFD700', category: 'entities' },
  { type: TileType.SPAWN_POINT, name: 'Player Spawn', color: '#32CD32', category: 'entities' },
  { type: TileType.TELEPORTER, name: 'Teleporter', color: '#9370DB', category: 'entities' },

  // Trigger tiles
  { type: TileType.TRAP, name: 'Trap', color: '#FF0000', category: 'triggers' },
  { type: TileType.SAFE_ZONE, name: 'Safe Zone', color: '#00FFFF', category: 'triggers' },
  { type: TileType.EXIT_ZONE, name: 'Exit Zone', color: '#FF00FF', category: 'triggers' },
];

// Helper Functions
export function getTileColor(tileType: TileType): string {
  const tile = DEFAULT_TILE_PALETTE.find(t => t.type === tileType);
  return tile?.color || '#000000';
}

/**
 * Get the default layer for a tile type based on its category
 * Used by editor to auto-switch layers when selecting tiles
 */
export function getTileLayer(tileType: TileType): LayerType {
  const tile = DEFAULT_TILE_PALETTE.find(t => t.type === tileType);
  if (!tile) return LayerType.GROUND;

  switch (tile.category) {
  case 'ground': return LayerType.GROUND;
  case 'collision': return LayerType.COLLISION;
  case 'objects': return LayerType.OBJECTS;
  case 'entities': return LayerType.ENTITIES;
  case 'triggers': return LayerType.TRIGGERS;
  default: return LayerType.GROUND;
  }
}

export function getTileName(tileType: TileType): string {
  const tile = DEFAULT_TILE_PALETTE.find(t => t.type === tileType);
  return tile?.name || 'Unknown';
}

// ========== ENTITY PLACEMENT HELPERS ==========

/**
 * Returns the Y coordinate (top of hitbox) for an entity to stand
 * exactly on a platform at the given ground Y.
 *
 * This eliminates the "coordinate gotcha" where authors copy the
 * player's `y - 40` pattern for entities with different heights.
 * `entity.y` is the TOP of the hitbox, NOT the feet — so to plant
 * a 130px-tall mob on a platform at GROUND_Y=700, use
 * `onGround(700, 130)` → 570, not `700 - 40` → 660.
 *
 * @param groundY - the platform's Y (top surface), in pixels
 * @param entityHeight - the entity's height in pixels
 * @returns the Y coordinate to use for entity.y (top of hitbox)
 */
export function onGround(groundY: number, entityHeight: number): number {
  return groundY - entityHeight;
}

/**
 * Returns the Y coordinate for an entity to float `offset` pixels
 * above a platform (for hanging/flying entities).
 *
 * @param groundY - the platform's Y (top surface), in pixels
 * @param entityHeight - the entity's height in pixels
 * @param offset - pixels above the platform surface (default 0)
 * @returns the Y coordinate to use for entity.y (top of hitbox)
 */
export function aboveGround(groundY: number, entityHeight: number, offset: number = 0): number {
  return groundY - entityHeight - offset;
}

// ========== SPARSE TILE STORAGE HELPERS ==========

/**
 * Encode tile coordinates to string key
 * Supports negative coordinates: encodeTileKey(-5, -10) = "-5,-10"
 */
export function encodeTileKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * Decode string key to tile coordinates
 * Returns {x, y} from key like "-5,-10"
 */
export function decodeTileKey(key: string): { x: number; y: number } {
  const [x, y] = key.split(',').map(Number);
  return { x, y };
}

/**
 * Get tile from sparse layer
 * Returns TileType.EMPTY (0) if no tile at position
 */
export function getTileFromLayer(layer: Layer, x: number, y: number): number {
  // Prefer new sparse format
  if (layer.tiles) {
    return layer.tiles.get(encodeTileKey(x, y)) || TileType.EMPTY;
  }
  // Fallback to legacy dense array
  if (layer.data && layer.data[y] && layer.data[y][x] !== undefined) {
    return layer.data[y][x];
  }
  return TileType.EMPTY;
}

/**
 * Set tile in sparse layer
 * Automatically removes tile if setting to EMPTY
 */
export function setTileInLayer(layer: Layer, x: number, y: number, tileType: TileType): void {
  // Initialize tiles Map if needed
  if (!layer.tiles) {
    layer.tiles = new Map();
  }

  const key = encodeTileKey(x, y);
  if (tileType === TileType.EMPTY) {
    layer.tiles.delete(key);
  } else {
    layer.tiles.set(key, tileType);
  }
}

/**
 * Delete tile at position
 * Returns true if tile existed and was deleted
 */
export function deleteTileAt(layer: Layer, x: number, y: number): boolean {
  if (!layer.tiles) {
    return false;
  }
  return layer.tiles.delete(encodeTileKey(x, y));
}

/**
 * Calculate bounds of placed tiles in a layer
 * Returns {minX, maxX, minY, maxY} or null if no tiles
 */
export function calculateLayerBounds(layer: Layer): { minX: number; maxX: number; minY: number; maxY: number } | null {
  // Use sparse tiles if available
  const tiles = layer.tiles;
  if (!tiles || tiles.size === 0) {
    // Fallback to legacy data
    if (!layer.data || layer.data.length === 0) return null;
    return {
      minX: 0,
      maxX: layer.data[0].length - 1,
      minY: 0,
      maxY: layer.data.length - 1
    };
  }

  const coords = Array.from(tiles.keys()).map(decodeTileKey);
  return {
    minX: Math.min(...coords.map(c => c.x)),
    maxX: Math.max(...coords.map(c => c.x)),
    minY: Math.min(...coords.map(c => c.y)),
    maxY: Math.max(...coords.map(c => c.y))
  };
}

/**
 * Convert legacy dense array to sparse Map
 * Used for migration from old format
 */
export function convertDenseToSparse(data: number[][]): Map<string, number> {
  const tiles = new Map<string, number>();
  for (let y = 0; y < data.length; y++) {
    for (let x = 0; x < data[y].length; x++) {
      if (data[y][x] !== TileType.EMPTY) {
        tiles.set(encodeTileKey(x, y), data[y][x]);
      }
    }
  }
  return tiles;
}

/**
 * Serialize tiles Map to JSON-compatible object
 * Map -> Object for JSON.stringify
 */
export function serializeTiles(tiles: Map<string, number>): Record<string, number> {
  return Object.fromEntries(tiles);
}

/**
 * Deserialize tiles from JSON object
 * Object -> Map for runtime use
 */
export function deserializeTiles(obj: Record<string, number>): Map<string, number> {
  return new Map(Object.entries(obj));
}

// 2D Converters moved to GridUtils (getTilePos, getWorldPos)
/**
 * Serialize a GridLevel for JSON storage
 * Converts Maps to plain objects for JSON.stringify compatibility
 */
export function serializeGridLevel(level: GridLevel): any {
  return {
    ...level,
    layers: level.layers.map(layer => ({
      ...layer,
      tiles: serializeTiles(layer.tiles),
      // Remove the runtime Map from serialization
      data: undefined
    }))
  };
}

/**
 * Deserialize a GridLevel from JSON
 * Converts plain objects back to Maps
 */
export function deserializeGridLevel(levelData: any): GridLevel {
  return {
    ...levelData,
    layers: levelData.layers.map((layer: any) => ({
      ...layer,
      tiles: deserializeTiles(layer.tiles || {}),
      // data field is optional in sparse format
      data: undefined
    }))
  };
}
